// Privacy Policy — drafted to satisfy the Digital Personal Data Protection Act
// 2023 (India) + GDPR Article 13. Last updated date drives the "What's new"
// nudge if we ever rev the policy.
import { Link } from "react-router-dom";

const LAST_UPDATED = "16 June 2026";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-white/10 bg-black/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="font-serif text-2xl text-[#D4AF37]" data-testid="header-home-link">Travelsate</Link>
          <Link to="/" className="text-white/60 hover:text-[#D4AF37] text-sm">← Home</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12" data-testid="privacy-policy-page">
        <h1 className="font-serif text-4xl text-[#D4AF37] mb-2">Privacy Policy</h1>
        <p className="text-white/40 text-xs mb-10">Last updated: {LAST_UPDATED}</p>

        <Section title="1. Who we are">
          Travelsate ("we", "us", "our") is operated from India and offers
          travel-booking services on travelsate.in. We process personal data in
          line with India's Digital Personal Data Protection Act 2023 and, where
          applicable, the EU General Data Protection Regulation.
        </Section>

        <Section title="2. Data we collect">
          <p className="mb-2">When you book or browse, we collect:</p>
          <ul className="list-disc list-inside space-y-1 text-white/70">
            <li>Identity: name, email, phone, pincode.</li>
            <li>Travel details: destination, dates, traveller names, genders, meal preferences, infant DOBs.</li>
            <li>Payment metadata: Razorpay order/subscription/payment IDs. We never see or store full card numbers — those stay inside Razorpay's PCI-certified vault.</li>
            <li>Technical: IP address, browser, device, cookies set for session continuity.</li>
          </ul>
        </Section>

        <Section title="3. How we use it">
          <ul className="list-disc list-inside space-y-1 text-white/70">
            <li>To create, fulfil, and service your bookings.</li>
            <li>To send transactional emails / SMS (confirmation, EMI reminders, cancellation, invoices).</li>
            <li>To run live flight/hotel pricing via SerpAPI and AI-generated itineraries via OpenAI/Anthropic — the destination + date is shared, never your name or contact.</li>
            <li>To detect fraud, deter abuse, and improve the service.</li>
          </ul>
        </Section>

        <Section title="4. Who we share it with">
          <p className="mb-2">Strictly limited processors who help us deliver the service:</p>
          <ul className="list-disc list-inside space-y-1 text-white/70">
            <li><strong>Razorpay</strong> — payment processing.</li>
            <li><strong>Twilio</strong> — phone OTP & SMS.</li>
            <li><strong>SendGrid</strong> — email OTP & transactional email.</li>
            <li><strong>SerpAPI</strong> — live flight / hotel / reviews lookup (destination only).</li>
            <li><strong>Google Maps</strong> — distance lookups (origin / destination cities only).</li>
            <li><strong>Cloud hosting</strong> on the Emergent platform.</li>
          </ul>
          <p className="mt-2">We do not sell or rent personal data.</p>
        </Section>

        <Section title="5. How long we keep it">
          Booking records are retained for 7 years (Indian Companies Act + tax-audit
          requirement). OTP codes are deleted within minutes of use. Marketing
          email opt-outs are honoured immediately.
        </Section>

        <Section title="6. Your rights">
          You can request access, correction, erasure (where lawful), or a copy of
          your data by writing to <a href="mailto:admin@travelsate.in" className="text-[#D4AF37] underline">admin@travelsate.in</a> or calling <a href="tel:+919972744308" className="text-[#D4AF37] underline">+91 99727 44308</a>.
          Under DPDP Act 2023 you may also nominate a representative to exercise
          these rights after death/incapacity, and lodge a complaint with the
          Data Protection Board of India.
        </Section>

        <Section title="7. Cookies">
          We use strictly necessary cookies for login session continuity (JWT in
          httpOnly cookie) and an analytics cookie that tracks page visits without
          identifying you. You can withdraw consent any time by clearing your
          browser data or via your browser's cookie controls.
        </Section>

        <Section title="8. Security">
          HTTPS site-wide, JWT bearer auth on every API, rate-limited OTP
          endpoints, signature-verified Razorpay webhooks, OWASP-standard
          security headers (HSTS, CSP, X-Frame-Options, etc.).
        </Section>

        <Section title="9. Changes">
          Material changes will be notified by email and posted here with an
          updated "Last updated" date.
        </Section>

        <Section title="10. Contact">
          Grievance / DPO: <a href="mailto:admin@travelsate.in" className="text-[#D4AF37] underline">admin@travelsate.in</a> · <a href="tel:+919972744308" className="text-[#D4AF37] underline">+91 99727 44308</a>
        </Section>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="font-serif text-xl text-white mb-3">{title}</h2>
      <div className="text-white/70 text-sm leading-relaxed">{children}</div>
    </section>
  );
}
